import "./network-status-bar.scss";
import { FC, memo, useEffect, useState } from "react";
import classNames from "classnames";
import { CheckFillIcon, CloseIcon, RemoveFillIcon } from "@components/icons";
import { useNetworkStatus } from "@hooks/use-network-status";
import { Button } from "@components/button";

const ONLINE_STATUS_AUTO_HIDE_DELAY = 5000;

const NetworkStatusBarComponent: FC = () => {
  const { isOnline, hasConnectionChanged } = useNetworkStatus();
  const [showNetworkStatus, setShowNetworkStatus] = useState<boolean>(() => !isOnline);

  const networkStatusMessage = isOnline
    ? "You are back online."
    : "You are offline. Some features may be unavailable.";

  useEffect(() => {
    if (!isOnline || hasConnectionChanged) {
      setShowNetworkStatus(true);
    }
  }, [hasConnectionChanged, isOnline]);

  useEffect(() => {
    if (!isOnline || !showNetworkStatus) {
      return;
    }

    const timer = window.setTimeout(() => {
      setShowNetworkStatus(false);
    }, ONLINE_STATUS_AUTO_HIDE_DELAY);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOnline, showNetworkStatus]);

  if (!showNetworkStatus) {
    return null;
  }

  return (
    <div
      className={classNames("network-status-bar", {
        online: isOnline,
        offline: !isOnline,
      })}
      role="status"
      aria-live="polite"
    >
      <div className="network-status-content">
        <span className="network-status-icon" aria-hidden="true">
          {isOnline ? (
            <CheckFillIcon color="#0c8c43" size="18" />
          ) : (
            <RemoveFillIcon color="#d43f3a" size="18" />
          )}
        </span>
        <span className="network-status-text">{networkStatusMessage}</span>
      </div>

      <Button
        type="button"
        className="network-status-close"
        aria-label="Dismiss network status"
        onClick={() => setShowNetworkStatus(false)}
      >
        <CloseIcon color={isOnline ? "#0c8c43" : "#d43f3a"} size={12} />
      </Button>
    </div>
  );
};

export const NetworkStatusBar = memo(NetworkStatusBarComponent);
